import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Spinner } from '@Components/Spinner';

import { conditionallyPluralizeWithCount } from '@src/utils/text-utils';
import { format, parseISO } from 'date-fns';
import classNames from 'classnames';

import { getAudit } from '../api';
import { AccountFilter } from '../components/AccountFilter';
import { ExportDialog } from '../components/ExportDialog';
import { FindingCard } from '../components/FindingCard';
import { PlatformFilter } from '../components/PlatformFilter';
import { ALL_TEMPLATES, TemplateTabs } from '../components/TemplateTabs';
import {
  ALL_PLATFORMS,
  PLATFORM_LABEL,
  sectionRank,
  severityRank,
  type AuditRun,
  type AuditStatus,
  type CidSummary,
  type Finding,
  type Platform,
  type PreviousAuditSummary,
  type Template
} from '../types';
import { formatAuditLabel } from '../utils/auditTitle';
import { useAuditBasePath } from '../utils/useAuditBasePath';

export const AuditDetail = () => {
  const { auditId } = useParams<{ auditId: string }>();

  const query = useQuery({
    queryKey: ['audit', auditId],
    queryFn: () => getAudit(auditId!),
    enabled: Boolean(auditId),
    refetchInterval: q => {
      const status = q.state.data?.run.status;
      return status === 'complete' || status === 'failed' ? false : 3000;
    }
  });

  const cidNameByCid = useMemo(() => {
    const cids = query.data?.run.cids ?? [];
    return new Map(cids.map(c => [c.cid, c.name]));
  }, [query.data?.run.cids]);

  if (!auditId) return null;
  if (query.isLoading) return <div className='audit-loading'>Loading audit…</div>;
  if (query.isError) return <div className='audit-error'>Failed to load this audit.</div>;
  if (!query.data) return null;

  const { run, findings, previousAudit } = query.data;

  return (
    <div className='audit-detail'>
      <AuditHeader run={run} previousAudit={previousAudit} findings={findings} />
      <AuditBody run={run} findings={findings} cidNameByCid={cidNameByCid} />
    </div>
  );
};

const AuditHeader = ({
  run,
  previousAudit,
  findings
}: {
  run: AuditRun;
  previousAudit: PreviousAuditSummary | null;
  findings: Finding[];
}) => {
  const windowLabel = `${format(parseISO(run.windowStart), 'MMM d, yyyy')} – ${format(parseISO(run.windowEnd), 'MMM d, yyyy')}`;
  const pulledAt = format(parseISO(run.triggeredAt), "MMM d, yyyy 'at' h:mm a");
  const inScopeAccounts = run.cids.filter(c => c.activeTemplates.length > 0);
  const platformsActive = [...new Set(inScopeAccounts.map(c => c.platform))];
  const [exportOpen, setExportOpen] = useState(false);

  return (
    <header className='audit-detail__header'>
      <div className='audit-detail__title-row'>
        <div className='audit-detail__title'>
          {format(parseISO(run.triggeredAt), 'MMMM d, yyyy')} audit
        </div>
        {run.status === 'complete' && (
          <button
            type='button'
            className='audit-detail__export-btn'
            onClick={() => setExportOpen(true)}
          >
            Export ↓
          </button>
        )}
      </div>
      {exportOpen && (
        <ExportDialog run={run} findings={findings} onClose={() => setExportOpen(false)} />
      )}
      <div className='audit-detail__meta'>
        <span>Away · Trailing 90 days · {windowLabel}</span>
        <span>·</span>
        <span>
          Sources:{' '}
          {platformsActive.map((p, i) => (
            <span key={p}>
              {i > 0 && ', '}
              {PLATFORM_LABEL[p]} ({inScopeAccounts.filter(c => c.platform === p).length})
            </span>
          ))}
        </span>
        <span>·</span>
        <span>Triggered {pulledAt}</span>
      </div>
      {previousAudit && (
        <DiffSummary previousAudit={previousAudit} findings={findings} auditId={run.id} />
      )}
      <StatusPill status={run.status} error={run.error} />
    </header>
  );
};

// Aggregate diff summary shown when a previous audit exists for the same company. Counts the
// per-finding diff statuses and surfaces them as a tight one-liner above the status pill.
const DiffSummary = ({
  previousAudit,
  findings,
  auditId
}: {
  previousAudit: PreviousAuditSummary;
  findings: Finding[];
  auditId: string;
}) => {
  const base = useAuditBasePath();
  const counts = { new: 0, changed: 0, unchanged: 0 };
  let escalated = 0;
  let deescalated = 0;
  for (const f of findings) {
    if (!f.diff) continue;
    if (f.diff.status === 'new') {
      counts.new += 1;
    } else if (f.diff.status === 'changed') {
      counts.changed += 1;
      if (f.diff.previousSeverity !== undefined && f.diff.previousSeverity !== f.severity) {
        const direction = severityRank(f.severity) - severityRank(f.diff.previousSeverity);
        if (direction > 0) {
          escalated += 1;
        } else if (direction < 0) {
          deescalated += 1;
        }
      }
    } else {
      counts.unchanged += 1;
    }
  }
  const prevDate = formatAuditLabel(previousAudit.triggeredAt);
  const anyDelta = counts.new + counts.changed > 0;
  return (
    <div className='audit-detail__diff-summary'>
      <span className='audit-detail__diff-label'>Compared to audit run {prevDate}:</span>
      {!anyDelta ? (
        <span className='audit-detail__diff-pill audit-detail__diff-pill--steady'>No changes</span>
      ) : (
        <>
          {counts.new > 0 && (
            <span className='audit-detail__diff-pill audit-detail__diff-pill--new'>
              {counts.new} new
            </span>
          )}
          {escalated > 0 && (
            <span className='audit-detail__diff-pill audit-detail__diff-pill--worse'>
              ↑ {escalated} escalated
            </span>
          )}
          {deescalated > 0 && (
            <span className='audit-detail__diff-pill audit-detail__diff-pill--better'>
              ↓ {deescalated} improved
            </span>
          )}
          {counts.changed - escalated - deescalated > 0 && (
            <span className='audit-detail__diff-pill audit-detail__diff-pill--lateral'>
              {counts.changed - escalated - deescalated} other
            </span>
          )}
        </>
      )}
      <Link to={`${base}/${auditId}/diff`} className='audit-detail__diff-link'>
        What changed →
      </Link>
    </div>
  );
};

const StatusPill = ({ status, error }: { status: AuditStatus; error: string | null }) => (
  <div className={`audit-detail__status audit-detail__status--${status}`}>
    {statusLabel(status)}
    {status === 'failed' && error && <span className='audit-detail__error-detail'> — {error}</span>}
  </div>
);

function statusLabel(status: AuditStatus): string {
  if (status === 'complete') return 'Complete';
  if (status === 'failed') return 'Failed';
  if (status === 'pending') return 'Queued';
  if (status === 'pulling') return 'Pulling data from Google Ads…';
  if (status === 'analyzing') return 'Running checks…';
  return status;
}

function progressText(status: AuditStatus): string {
  if (status === 'pending') return 'Audit queued — the runner will pick this up in a moment.';
  if (status === 'pulling')
    return 'Pulling campaign configuration and detecting active templates for each account…';
  if (status === 'analyzing') return 'Running structural checks against the pulled data.';
  return 'Preparing…';
}

interface AuditBodyProps {
  run: AuditRun;
  findings: Finding[];
  cidNameByCid: Map<string, string>;
}

const AuditBody = ({ run, findings, cidNameByCid }: AuditBodyProps) => {
  // ----- Derived metadata (hooks always run; safe to call before early returns) -----
  const activeAccountCountByTemplate = useMemo(() => {
    const out: Record<Template, number> = { search: 0, shopping: 0, pmax: 0 };
    for (const c of run.cids) for (const t of c.activeTemplates) out[t] += 1;
    return out;
  }, [run.cids]);

  const findingCountByTemplate = useMemo(() => {
    const out: Record<Template, number> = { search: 0, shopping: 0, pmax: 0 };
    for (const f of findings) out[f.template] += 1;
    return out;
  }, [findings]);

  // ----- Deep-link focus (e.g. arriving from the "What changed" diff view) -----
  // ?template=&section=&cid=&checkId= → land on that template+section and highlight the finding.
  const [searchParams] = useSearchParams();
  const focusTemplateParam = searchParams.get('template') as Template | null;
  const focusSection = searchParams.get('section');
  const focusCid = searchParams.get('cid');
  const focusCheckId = searchParams.get('checkId');
  const focusKey = focusCid && focusCheckId ? `${focusCid}|${focusCheckId}` : null;

  // ----- UI state -----
  const firstEnabledTemplate =
    ALL_TEMPLATES.find(t => activeAccountCountByTemplate[t] > 0) ?? 'search';
  const [selectedTemplate, setSelectedTemplate] = useState<Template>(() =>
    focusTemplateParam &&
    ALL_TEMPLATES.includes(focusTemplateParam) &&
    activeAccountCountByTemplate[focusTemplateParam] > 0
      ? focusTemplateParam
      : firstEnabledTemplate
  );

  // When activeTemplates first arrive (post-detection), snap to the first enabled template
  // exactly once if the current selection has no accounts.
  useEffect(() => {
    if (
      activeAccountCountByTemplate[selectedTemplate] === 0 &&
      activeAccountCountByTemplate[firstEnabledTemplate] > 0
    ) {
      setSelectedTemplate(firstEnabledTemplate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstEnabledTemplate]);

  const [selectedPlatforms, setSelectedPlatforms] = useState<Set<Platform>>(
    () => new Set(ALL_PLATFORMS)
  );

  // Accounts that have the currently-selected template active AND fall under the platform filter.
  const accountsForCurrent = useMemo(
    () =>
      run.cids.filter(
        c => c.activeTemplates.includes(selectedTemplate) && selectedPlatforms.has(c.platform)
      ),
    [run.cids, selectedTemplate, selectedPlatforms]
  );

  // Account chip selection. Defaults to "all accounts for this (template, platform)" and resets
  // whenever the upstream filters change so the user doesn't carry stale selections across tabs.
  const [selectedCids, setSelectedCids] = useState<Set<string>>(
    () => new Set(accountsForCurrent.map(a => a.cid))
  );
  useEffect(() => {
    setSelectedCids(new Set(accountsForCurrent.map(a => a.cid)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplate, selectedPlatforms, run.cids]);

  // Platform counts (for the segmented control badges) — counted as **accounts** that have the
  // current template active. We deliberately don't count findings here: a single account can
  // produce many findings, and users read the chip as "this platform has N accounts" not
  // "this platform has N findings."
  const platformCountsForTemplate = useMemo(() => {
    const out: Record<Platform, number> = { google_ads: 0, microsoft_ads: 0 };
    for (const c of run.cids) {
      if (!c.activeTemplates.includes(selectedTemplate)) continue;
      out[c.platform] += 1;
    }
    return out;
  }, [run.cids, selectedTemplate]);

  const visibleFindings = useMemo(
    () =>
      findings.filter(
        f =>
          f.template === selectedTemplate &&
          selectedPlatforms.has(f.platform) &&
          selectedCids.has(f.cid)
      ),
    [findings, selectedTemplate, selectedPlatforms, selectedCids]
  );

  // Findings grouped by section, in canonical section order, with rows clustered across
  // accounts. Drives the section rail, the Overview scorecard, and the single-section content
  // pane — all from one filtered, sorted source so the three stay perfectly consistent.
  const sectionGroups = useMemo(
    () =>
      buildSectionGroups(
        visibleFindings,
        accountsForCurrent.map(a => a.cid)
      ),
    [visibleFindings, accountsForCurrent]
  );

  // Section navigation. 'overview' is the scorecard landing; otherwise one section's content
  // replaces the view (one section at a time — no long vertical scroll across sections).
  // Initialize to a deep-linked section when present, else the Overview.
  const [selectedSection, setSelectedSection] = useState<string>(focusSection || 'overview');

  // Reset to the Overview whenever the template ACTUALLY changes — a new template is a fresh
  // scorecard. Compare against the previous value (rather than a "skip first mount" flag) so this
  // is StrictMode-safe: a double-invoked mount effect sees the same template and won't clobber a
  // deep-linked section.
  const prevTemplateRef = useRef(selectedTemplate);
  useEffect(() => {
    if (prevTemplateRef.current !== selectedTemplate) {
      prevTemplateRef.current = selectedTemplate;
      setSelectedSection('overview');
    }
  }, [selectedTemplate]);

  // If the active filters make the selected section disappear, fall back to the Overview rather
  // than rendering an empty pane.
  useEffect(() => {
    if (selectedSection !== 'overview' && !sectionGroups.some(g => g.section === selectedSection)) {
      setSelectedSection('overview');
    }
  }, [sectionGroups, selectedSection]);

  // ----- Render -----
  if (run.status !== 'complete' && run.status !== 'failed') {
    return (
      <section className='audit-detail__body'>
        <div className='audit-detail__progress'>
          <div className='audit-detail__progress-spinner'>
            <Spinner size={20} />
          </div>
          <div className='audit-detail__progress-text'>{progressText(run.status)}</div>
        </div>
      </section>
    );
  }

  if (run.status === 'failed') {
    return (
      <section className='audit-detail__body'>
        <div className='audit-detail__failed'>
          <div className='audit-detail__failed-title'>This audit didn't complete.</div>
          {run.error && <div className='audit-detail__failed-reason'>{run.error}</div>}
        </div>
      </section>
    );
  }

  return (
    <section className='audit-detail__body'>
      <TemplateTabs
        selected={selectedTemplate}
        activeAccountCountByTemplate={activeAccountCountByTemplate}
        onChange={setSelectedTemplate}
      />
      <div className='audit-detail__filters'>
        <PlatformFilter
          selected={selectedPlatforms}
          counts={platformCountsForTemplate}
          onChange={setSelectedPlatforms}
        />
        <AccountFilter
          accounts={accountsForCurrent}
          selected={selectedCids}
          onChange={setSelectedCids}
        />
      </div>
      {visibleFindings.length === 0 ? (
        <EmptyForTemplate
          template={selectedTemplate}
          accountsForCurrent={accountsForCurrent}
          findingsForTemplate={findingCountByTemplate[selectedTemplate]}
        />
      ) : (
        <div className='audit-workspace'>
          <SectionRail
            sections={sectionGroups}
            selected={selectedSection}
            onSelect={setSelectedSection}
          />
          <div className='audit-workspace__content'>
            {selectedSection === 'overview' ? (
              <OverviewScorecard
                sectionGroups={sectionGroups}
                cids={accountsForCurrent}
                onSelect={setSelectedSection}
              />
            ) : (
              <SectionContent
                group={sectionGroups.find(g => g.section === selectedSection)!}
                auditId={run.id}
                cidNameByCid={cidNameByCid}
                focusKey={focusKey}
              />
            )}
          </div>
        </div>
      )}
    </section>
  );
};

interface SectionGroupData {
  section: string;
  items: Finding[];
}

// Group findings by section (canonical order) and cluster each section's rows across accounts.
// Shared by the rail, scorecard, and section pane so all three derive from one ordering.
function buildSectionGroups(findings: Finding[], cidOrder: string[]): SectionGroupData[] {
  const cidRank = new Map(cidOrder.map((cid, i) => [cid, i]));
  const bySection = new Map<string, Finding[]>();
  for (const f of findings) {
    const arr = bySection.get(f.section) ?? [];
    arr.push(f);
    bySection.set(f.section, arr);
  }
  return [...bySection.entries()]
    .map(([section, items]) => ({
      section,
      items: items.slice().sort((a, b) => {
        if (a.templateRow !== b.templateRow) return a.templateRow.localeCompare(b.templateRow);
        return (cidRank.get(a.cid) ?? 99) - (cidRank.get(b.cid) ?? 99);
      })
    }))
    .sort((a, b) => {
      const r = sectionRank(a.section) - sectionRank(b.section);
      return r !== 0 ? r : a.section.localeCompare(b.section);
    });
}

// Left rail of section navigators. Each item carries a compact severity rollup so the rail
// doubles as a "where are the problems?" overview. 'Overview' sits at the top.
const SectionRail = ({
  sections,
  selected,
  onSelect
}: {
  sections: SectionGroupData[];
  selected: string;
  onSelect: (s: string) => void;
}) => (
  <nav className='section-rail' aria-label='Audit sections'>
    <div className='section-rail__label'>Sections</div>
    <button
      type='button'
      className={classNames('section-rail__item', {
        'section-rail__item--active': selected === 'overview'
      })}
      onClick={() => onSelect('overview')}
    >
      <span className='section-rail__name'>Overview</span>
    </button>
    {/* Overview is the dashboard tier — set it off from the section list with one divider. */}
    <div className='section-rail__divider' aria-hidden />
    {sections.map(g => {
      const counts = severityCounts(g.items);
      return (
        <button
          key={g.section}
          type='button'
          className={classNames('section-rail__item', {
            'section-rail__item--active': selected === g.section
          })}
          onClick={() => onSelect(g.section)}
        >
          <span className='section-rail__name'>{g.section}</span>
          <RollupChips counts={counts} />
        </button>
      );
    })}
  </nav>
);

// Compact severity rollup for the rail: a colored count chip per non-zero actionable bucket,
// or a single clean check when there's nothing actionable. Deliberately terser than the
// word-pills used in section/row headers — the rail needs to stay narrow and scannable.
const RollupChips = ({ counts }: { counts: FindingCounts }) => {
  const actionable = counts.large + counts.medium + counts.small;
  if (actionable === 0) {
    return (
      <span className='rollup-chip rollup-chip--clean' title='No issues' aria-label='No issues'>
        ✓
      </span>
    );
  }
  return (
    <span className='rollup-chips'>
      {counts.large > 0 && (
        <span className='rollup-chip rollup-chip--large' title={`${counts.large} large`}>
          {counts.large}
        </span>
      )}
      {counts.medium > 0 && (
        <span className='rollup-chip rollup-chip--medium' title={`${counts.medium} medium`}>
          {counts.medium}
        </span>
      )}
      {counts.small > 0 && (
        <span className='rollup-chip rollup-chip--small' title={`${counts.small} small`}>
          {counts.small}
        </span>
      )}
    </span>
  );
};

// The Overview landing: the global SummaryBar headline + a grid of section scorecards. Clicking
// a card drills into that section. Recovers the at-a-glance, cross-section view that switching
// (one section at a time) would otherwise hide.
const OverviewScorecard = ({
  sectionGroups,
  cids,
  onSelect
}: {
  sectionGroups: SectionGroupData[];
  cids: CidSummary[];
  onSelect: (s: string) => void;
}) => {
  const allFindings = useMemo(() => sectionGroups.flatMap(g => g.items), [sectionGroups]);
  return (
    <div className='overview'>
      <SummaryBar findings={allFindings} cids={cids} />
      <div className='overview__grid'>
        {sectionGroups.map(g => {
          const counts = severityCounts(g.items);
          return (
            <button
              key={g.section}
              type='button'
              className='overview-card'
              onClick={() => onSelect(g.section)}
            >
              <div className='overview-card__title'>{g.section}</div>
              <div className='overview-card__count'>
                {g.items.length} {g.items.length === 1 ? 'finding' : 'findings'}
              </div>
              <SeverityPills counts={counts} kind='section' />
            </button>
          );
        })}
      </div>
    </div>
  );
};

// One section's full content: a header with its severity rollup, then the rows clustered across
// accounts (the same RowGroup treatment as before, just no longer wrapped in a collapsible
// SectionGroup — the rail now owns section navigation).
const SectionContent = ({
  group,
  auditId,
  cidNameByCid,
  focusKey
}: {
  group: SectionGroupData;
  auditId: string;
  cidNameByCid: Map<string, string>;
  focusKey?: string | null;
}) => {
  const counts = useMemo(() => severityCounts(group.items), [group.items]);
  const subgroups = useMemo(() => {
    const byRow = new Map<string, Finding[]>();
    for (const f of group.items) {
      const arr = byRow.get(f.templateRow) ?? [];
      arr.push(f);
      byRow.set(f.templateRow, arr);
    }
    return [...byRow.entries()].map(([templateRow, items]) => ({ templateRow, items }));
  }, [group.items]);

  return (
    <section className='section-content'>
      <header className='section-content__header'>
        <h2 className='section-content__title'>{group.section}</h2>
        <span className='section-content__count'>
          {group.items.length} {group.items.length === 1 ? 'finding' : 'findings'}
        </span>
        <SeverityPills counts={counts} kind='section' />
      </header>
      <div className='section-content__body'>
        {subgroups.map(sg => (
          <RowGroup
            key={sg.templateRow}
            templateRow={sg.templateRow}
            findings={sg.items}
            auditId={auditId}
            cidNameByCid={cidNameByCid}
            focusKey={focusKey}
          />
        ))}
      </div>
    </section>
  );
};

const EmptyForTemplate = ({
  template,
  accountsForCurrent,
  findingsForTemplate
}: {
  template: Template;
  accountsForCurrent: CidSummary[];
  findingsForTemplate: number;
}) => {
  const templateLabel =
    template === 'search' ? 'Search' : template === 'shopping' ? 'Shopping' : 'Performance Max';

  if (accountsForCurrent.length === 0) {
    return (
      <div className='audit-detail__placeholder'>
        <div className='audit-detail__placeholder-title'>No accounts in scope</div>
        <div className='audit-detail__placeholder-hint'>
          The current platform/account filter excludes everything for {templateLabel}. Adjust the
          filter above, or pick another template tab.
        </div>
      </div>
    );
  }
  if (findingsForTemplate === 0) {
    return (
      <div className='audit-detail__placeholder'>
        <div className='audit-detail__placeholder-title'>No {templateLabel} findings</div>
        <div className='audit-detail__placeholder-hint'>
          {conditionallyPluralizeWithCount(accountsForCurrent.length, 'account')} ran{' '}
          {templateLabel} campaigns, but this audit produced no {templateLabel} findings for them.
        </div>
      </div>
    );
  }
  return (
    <div className='audit-detail__placeholder'>
      <div className='audit-detail__placeholder-title'>No findings match the current filter</div>
      <div className='audit-detail__placeholder-hint'>
        Try selecting more accounts or platforms above.
      </div>
    </div>
  );
};

const SummaryBar = ({ findings, cids }: { findings: Finding[]; cids: CidSummary[] }) => {
  const counts = useMemo(() => {
    const out = { large: 0, medium: 0, small: 0, no_issue: 0, unavailable: 0 };
    for (const f of findings) {
      if (f.status === 'unavailable') {
        out.unavailable += 1;
      } else if (f.severity === 'large') {
        out.large += 1;
      } else if (f.severity === 'medium') {
        out.medium += 1;
      } else if (f.severity === 'small') {
        out.small += 1;
      } else if (f.severity === 'no_issue') {
        out.no_issue += 1;
      }
    }
    return out;
  }, [findings]);

  return (
    <div className='summary-bar'>
      <div className='summary-bar__title'>Summary</div>
      <div className='summary-bar__counts'>
        <SummaryCount label='Large' count={counts.large} kind='large' />
        <SummaryCount label='Medium' count={counts.medium} kind='medium' />
        <SummaryCount label='Small' count={counts.small} kind='small' />
        <SummaryCount label='Clean' count={counts.no_issue} kind='no_issue' />
        <SummaryCount label='Unavailable' count={counts.unavailable} kind='unavailable' />
      </div>
      <div className='summary-bar__cids'>
        {conditionallyPluralizeWithCount(cids.length, 'account')}:{' '}
        {cids.map(c => c.name).join(', ')}
      </div>
    </div>
  );
};

const SummaryCount = ({ label, count, kind }: { label: string; count: number; kind: string }) => (
  <div className={`summary-count summary-count--${kind}`}>
    <div className='summary-count__num'>{count}</div>
    <div className='summary-count__lbl'>{label}</div>
  </div>
);

const RowGroup = ({
  templateRow,
  findings,
  auditId,
  cidNameByCid,
  focusKey
}: {
  templateRow: string;
  findings: Finding[];
  auditId: string;
  cidNameByCid: Map<string, string>;
  focusKey?: string | null;
}) => {
  const counts = useMemo(() => severityCounts(findings), [findings]);
  return (
    <div className='findings-row-group'>
      <h3 className='findings-row-group__header'>
        <span className='findings-row-group__title'>{templateRow}</span>
        <span className='findings-row-group__count'>
          {findings.length} {findings.length === 1 ? 'finding' : 'findings'}
        </span>
        <SeverityPills counts={counts} kind='row' />
      </h3>
      <div className='findings-row-group__cards'>
        {findings.map(f => (
          <FindingCard
            key={f.id}
            finding={f}
            cidName={cidNameByCid.get(f.cid) ?? f.cid}
            auditId={auditId}
            focused={focusKey === `${f.cid}|${f.checkId}`}
          />
        ))}
      </div>
    </div>
  );
};

interface FindingCounts {
  large: number;
  medium: number;
  small: number;
  no_issue: number;
  unavailable: number;
}

// Shared severity rollup pills used by both section and sub-section (row) headers.
// Shows every non-zero bucket so a header with only clean/unavailable findings still tells
// the auditor what's in there (otherwise a section like Ad Copy with one no_issue + two
// unavailable findings would render with an empty pill area).
// `kind` only changes the BEM modifier so each scale can have its own visual treatment.
const SeverityPills = ({ counts, kind }: { counts: FindingCounts; kind: 'section' | 'row' }) => {
  const base = kind === 'section' ? 'findings-section__sev' : 'findings-row-group__sev';
  const pillBase = `${base}-pill`;
  return (
    <span className={base}>
      {counts.large > 0 && (
        <span className={`${pillBase} ${pillBase}--large`}>{counts.large} large</span>
      )}
      {counts.medium > 0 && (
        <span className={`${pillBase} ${pillBase}--medium`}>{counts.medium} medium</span>
      )}
      {counts.small > 0 && (
        <span className={`${pillBase} ${pillBase}--small`}>{counts.small} small</span>
      )}
      {counts.no_issue > 0 && (
        <span className={`${pillBase} ${pillBase}--clean`}>{counts.no_issue} clean</span>
      )}
      {counts.unavailable > 0 && (
        <span className={`${pillBase} ${pillBase}--unavailable`}>
          {counts.unavailable} unavailable
        </span>
      )}
    </span>
  );
};

function severityCounts(findings: Finding[]): FindingCounts {
  const out: FindingCounts = { large: 0, medium: 0, small: 0, no_issue: 0, unavailable: 0 };
  for (const f of findings) {
    if (f.status === 'unavailable') {
      out.unavailable += 1;
      continue;
    }
    if (
      f.severity === 'large' ||
      f.severity === 'medium' ||
      f.severity === 'small' ||
      f.severity === 'no_issue'
    ) {
      out[f.severity] += 1;
    }
    // 'na' severities that aren't also status=unavailable are extremely rare; if one slips
    // through it simply doesn't increment any counter — preferable to a misleading bucket.
  }
  return out;
}
